import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo, Children, isValidElement, useRef, useState, useEffect, useCallback, useLayoutEffect, forwardRef, cloneElement } from 'react';
import clsx from 'clsx';
import FocusTrap from 'focus-trap-react';
import { IconTickLarge } from '../../../../foundation/icons/IconTickLarge/index.js';
import { SelectSizesXL } from '../sizes/index.js';
import { useStyles } from '../styles/index.js';
import { useOutsideScroll } from '../../../hooks/src/useOutsideScroll/index.js';
import { SuggestionMenuPositions } from '../../../atoms/SuggestionMenu/types/index.js';
import { InlineButtonAlignments, InlineButtonVariants } from '../../../atoms/InlineButton/types/index.js';
import { SuggestionMenu } from '../../../atoms/SuggestionMenu/SuggestionMenu/index.js';
import { BottomSheet } from '../../../atoms/BottomSheet/BottomSheet/index.js';
import { computeResponsiveness } from '../../../../foundation/responsive/helpers/index.js';
import { useClickOutsideRef } from '../../../hooks/src/useClickOutsideRef/index.js';
import { isMobileDevice, isDesktopDevice } from '../../../../foundation/utils/getDeviceType/getDeviceType/index.js';
import { InlineButton } from '../../../atoms/InlineButton/InlineButton/index.js';

const padding = 24;
const withForwardRef = (WrappedComponent) => forwardRef((props, ref) => (jsx(WrappedComponent, { ...props, ref: ref })));
const calculateMaxHeight = (height, count, size, scrollHeight) => {
    if (scrollHeight || count) {
        const padding = 8;
        return ((count * size) + (count * padding));
    }
    return height;
};
const toggleSelection = (prevState, item, onChange, event) => {
    const newState = prevState.includes(item)
        ? prevState.filter(i => i !== item)
        : [...prevState, item];
    onChange?.(newState, event);
    return newState;
};
const Select = ({ children, size = SelectSizesXL, dataTestId, value, className, label = 'Title', startIcon, name, count, defaultValue, defaultSelected, zIndex = 999999, isDisabled, onChange, onClose, onClick, onOpenHandler, portalRef, pulseName, autoCloseOnScroll, isWithoutBorder, isWithoutBackground, isMultiselect, dataPulseComponent, dataPulseName, dataPulseComponentType, pulseSection, ...restProps }) => {
    const filteredValue = useMemo(() => Children.toArray(children).find((item) => {
        if (!isValidElement(item)) {
            return false;
        }
        return item.props.value === defaultValue;
    }), [children, defaultValue]);
    const sizes = computeResponsiveness(size);
    const buttonRef = useRef(null);
    const [contentRef, setContentRef] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(label);
    const [selectedIcon, setSelectedIcon] = useState(null);
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [position, setPosition] = useState(null);
    const [scrollHeight, setScrollHeight] = useState(0);
    const [selectedValue, setSelectedValue] = useState();
    const [selectedValues, setSelectedValues] = useState([]);
    const [selectedLabels, setSelectedLabels] = useState([]);
    const isControlled = useMemo(() => !!value, [value]);
    const styles = useStyles({
        sizes, scrollHeight, count,
    });
    useEffect(() => setSelected(label), [label]);
    const handleClose = () => {
        setIsOpen(false);
        onClose?.();
    };
    const buttonContentRef = useClickOutsideRef(() => {
        if (isOpen) {
            handleClose();
        }
    });
    const handleOutsideScroll = () => {
        if (!isDesktopDevice)
            return;
        if (isOpen) {
            handleClose();
        }
    };
    useOutsideScroll(contentRef, handleOutsideScroll, autoCloseOnScroll);
    const handleContentPosition = useCallback(() => {
        const contentPosition = contentRef?.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const maxHeight = calculateMaxHeight(Number(contentPosition?.height), Number(count), Number(sizes?.Default.value?.height), Number(scrollHeight));
        const bottom = !count ? Number(contentPosition?.bottom) : Number(contentPosition?.bottom) - (Number(contentPosition?.height) - maxHeight);
        if (!isMobileDevice) {
            setPosition((bottom) > windowHeight
                ? SuggestionMenuPositions.Top : SuggestionMenuPositions.Bottom);
        }
    }, [contentRef, count, scrollHeight, sizes]);
    useLayoutEffect(() => {
        const contentPosition = contentRef?.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        if (contentPosition && contentPosition?.top <= contentPosition?.height && !count) {
            setScrollHeight(windowHeight - (Number(contentPosition?.top) - padding));
        }
    }, [count, contentRef]);
    const toggleSelect = () => {
        if (!isOpen) {
            handleContentPosition();
        }
        else {
            setPosition(null);
        }
        setIsOpen(prev => !prev);
        if (isOpen) {
            onClose?.();
        }
    };
    useEffect(() => onOpenHandler?.(isOpen), [isOpen, onOpenHandler]);
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
                onClose?.();
            }
        };
        window.addEventListener('keydown', handleEscKey, true);
        return () => {
            window.removeEventListener('keydown', handleEscKey, true);
        };
    }, [isOpen, onClose]);
    const handleContentRef = (ref) => {
        setContentRef(ref);
    };
    const handleChange = useCallback((e) => {
        if (e.currentTarget instanceof HTMLElement) {
            const value = e.currentTarget.getAttribute('data-value');
            const label = e.currentTarget.textContent;
            if (!value || !label)
                return;
            if (isMultiselect) {
                value && setSelectedValues(prevValues => toggleSelection(prevValues, value, onChange, e));
                setSelectedLabels(prevLabels => toggleSelection(prevLabels, label, undefined, e));
            }
            else {
                setSelectedValues([value]);
                setSelectedLabels([label]);
                setIsOpen(false);
                onChange?.(value, e);
            }
        }
    }, [isMultiselect, onChange]);
    const currentValue = useMemo(() => {
        if (!isControlled)
            return selected;
        const matched = Children.toArray(children).find((child) => isValidElement(child) && child.props?.value === value);
        return matched?.props?.children ?? selected;
    }, [isControlled, children, selected, value]);
    const getChild = useCallback((child, index) => {
        const childValue = child.props.children;
        const StartIcon = withForwardRef(child.props.startIcon);
        const isActive = isMultiselect
            ? selectedValues.toString().includes(child.props.value)
            : selectedValue === child.props.value;
        const childBadge = child.props.badge;
        return cloneElement(child, {
            key: index,
            onClick: (e) => {
                e.stopPropagation();
                setSelected(childValue);
                if (!isMultiselect) {
                    setSelectedIcon(child.props.startIcon ? StartIcon : null);
                    setIsOpen(false);
                    setSelectedBadge(childBadge);
                }
                setSelectedValue(child?.props?.value);
                handleChange(e);
                if (isOpen) {
                    onClose?.();
                }
            },
            isActive,
            size,
            endIcon: isActive && isMultiselect ? IconTickLarge : child.props.endIcon,
        });
    }, [isMultiselect, selectedValues, selectedValue, size, handleChange, isOpen, onClose]);
    const content = useMemo(() => (children ? Children.map(children, getChild) : null), [children, getChild]);
    const defaultSelect = useMemo(() => content?.find(item => item?.props.value === defaultSelected), [content, defaultSelected]);
    const displayValue = isMultiselect
        ? selectedLabels.join(', ') || label
        : currentValue || filteredValue?.props?.children || label;
    useEffect(() => {
        if (!defaultSelected)
            return;
        setSelected(defaultSelect?.props.children);
        !isMultiselect && setSelectedIcon(defaultSelect?.props.startIcon || null);
        setSelectedBadge(defaultSelect?.props.badge);
        setSelectedValue(defaultSelect?.props?.value);
        setSelectedValues([defaultSelect?.props.value.toString()]);
        setSelectedLabels([defaultSelect?.props.children]);
    }, [defaultSelect?.props.badge, defaultSelect?.props.children, defaultSelect?.props.startIcon, defaultSelect?.props.value, defaultSelected, isMultiselect]);
    return (jsxs("div", { ...restProps, className: clsx(styles.root, className), "data-testid": dataTestId || 'select', ref: buttonContentRef, onClick: onClick, children: [jsx(InlineButton, { variant: InlineButtonVariants.Select, size: size, "data-pulse-name": dataPulseName, alignment: InlineButtonAlignments.Start, onClick: toggleSelect, isActive: isOpen, ref: buttonRef, isWithoutBackground: isWithoutBackground, isWithoutBorder: isWithoutBorder, "data-testid": "select-head", startIcon: startIcon || selectedIcon, ...(selectedBadge ? { badge: selectedBadge } : {}), isFullWidth: true, type: "button", isDisabled: isDisabled, value: selectedValue, ...(pulseName && { 'data-pulse-name': pulseName }), children: displayValue }), jsx("input", { tabIndex: -1, name: name, type: "hidden", value: currentValue }), jsx(SuggestionMenu, { size: size, relativeRef: buttonRef, isOpen: true, className: clsx(styles.menuContainer, styles.inVisibleContent), position: SuggestionMenuPositions.Bottom, children: jsx("div", { className: styles.menu, ref: handleContentRef, children: content && Children.map(content, child => cloneElement(child, {
                        disableSpread: true,
                    })) }) }), isDesktopDevice ? (jsx(SuggestionMenu, { id: "select-wrapper", size: size, portalRef: portalRef, relativeRef: buttonRef, dataTestId: "desktop-menu", isOpen: isOpen, zIndex: zIndex, "data-pulse-component": dataPulseComponent, "data-pulse-component-type": dataPulseComponentType, ...((position) ? { position } : {}), className: styles.menuContainer, isForSelect: true, hasOutsideClick: true, "data-pulse-section": pulseSection, children: content?.length ? (jsx(FocusTrap, { focusTrapOptions: {
                        clickOutsideDeactivates: true,
                    }, children: jsx("div", { className: styles.menu, ref: handleContentRef, children: content }) })) : null })) : (jsx(BottomSheet, { title: label, contentClassName: styles.mobileMenu, isOpen: isOpen, onClose: handleClose, hideHandleLine: true, zIndex: zIndex, "data-testid": "bottom-sheet", children: content }))] }));
};
Select.displayName = 'Select';

export { Select };
